import { QuillUtils } from "./quill-utils";
import { ElementType, ContentElement, ContentWithNotes } from "./commonTypes";
import { getUrl } from 'aws-amplify/storage';
import { openDB, IDBPDatabase } from 'idb';
import { Injectable } from "@angular/core";

const DB_NAME = 'journey-contentDB';
const CONTENT_CACHE_STORE = 'content-cache';

@Injectable({
    providedIn: 'root'
})
export class ContentUtilsService {
    private db: IDBPDatabase | null = null;

    /** ✅ Open IndexedDB only once */
    private async initDB() {
        if (!this.db) {
            this.db = await openDB(DB_NAME, 1, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains(CONTENT_CACHE_STORE)) {
                        db.createObjectStore(CONTENT_CACHE_STORE);
                    }
                },
            });
        }
    }

    /** ✅ Parse content string into ContentElement[] */
    parseContent(contentString: string): ContentElement[] {
        return QuillUtils.parseStringQuill(contentString);
    }

    /** ✅ Generate pre-signed video links asynchronously */
    async generateVideoLinks(elements: ContentElement[]): Promise<void> {
        try {
            const promises = elements.map(async (e) => {
                if (e.type === ElementType.VIDEO && e.content) {
                    try {
                        // const link = await getUrl({ options: { expiresIn: 36000 }, path: `${e.content}` });
                        // e.awsVideoUrl = link.url.toString();
                        e.awsVideoUrl = e.content;
                    } catch (err) {
                        console.error(`Error generating signed URL for video link [${e.content}]`, err);
                    }
                }
            });

            await Promise.all(promises); // ✅ Waits for all video links to be generated
        } catch (error) {
            console.error("Error processing video links:", error);
        }
    }

    /**
     * Search for chat and notes in the content's dynamic elements
     * @param elements 
     * @param chats 
     * @param notes 
     */
    async fillNoteAndChatIds(elements: ContentElement[], chats:{id:string,alias:string}[], notes:{id:string,alias:string}[]): Promise<void> {
        elements.forEach(e=>{
            if (e.type === ElementType.GROUP_CHAT) {
                e.id=chats.find(c=>c.alias===e.alias)?.id;
                console.log(`Found chat ID [${e.id}]`);
            }
            if (e.type === ElementType.PERSONAL_NOTE) {
                e.id=notes.find(n=>n.alias===e.alias)?.id;
                console.log(`Found note ID [${e.id}]`);
            }
        });
    }

    /** ✅ Get content from IndexedDB */
    async getContentFromCache(contentId: string): Promise<ContentWithNotes | null> {
        await this.initDB();
        return this.db ? await this.db.get(CONTENT_CACHE_STORE, contentId) : null;
    }

    /** ✅ Store content in IndexedDB */
    async cacheContent(contentId: string, data: ContentWithNotes) {
        await this.initDB();
        if (this.db) {
            await this.db.put(CONTENT_CACHE_STORE, data, contentId);
        }
    }
}
